import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import GenericForm from '../../components/GenericForm/GenericForm';
import Spinner from '../../components/Spinner/Spinner';
import styles from './Login.module.css';
import { login as apiLogin, forgotPassword, resendConfirmationCode, confirmResetRequest } from '../../services/ApiService';
import { useAuth } from '../../context/AuthContext';
import { ApiError, ErrorType } from '../../models/ApiError';
import { ErrorTypeHandler } from '../../utils/ErrorTypeHandler';
import { FormErrorHandler } from '../../utils/FormErrorHandler';
import { Snackbar, Alert } from '@mui/material';
import { LoginRequest } from '../../models/SafetyPortalTypes';

const Login = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const auth = useAuth();

  const [formData, setFormData] = useState({
    userName: '',
    password: '',
    newPassword: '',
    confirmationCode: '',
  });

  const [errors, setErrors] = useState<{
    userName: string;
    password: string;
    newPassword: string;
    confirmationCode: string;
  }>({
    userName: '',
    password: '',
    newPassword: '',
    confirmationCode: '',
  });

  const [isNewPasswordRequired, setIsNewPasswordRequired] = useState(false);
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'info' | 'warning' | 'error';
  }>({
    open: false,
    message: '',
    severity: 'info',
  });

  const validatePassword = (password: string): string => {
    const rules = [
      { regex: /[A-Z]/, message: t('Login.Errors.Password.Uppercase') },
      { regex: /[a-z]/, message: t('Login.Errors.Password.Lowercase') },
      { regex: /\d/, message: t('Login.Errors.Password.Number') },
      { regex: /[\W_]/, message: t('Login.Errors.Password.Symbol') },
      { regex: /.{8,}/, message: t('Login.Errors.Password.Length') },
    ];

    for (const rule of rules) {
      if (!rule.regex.test(password)) return rule.message;
    }

    return '';
  };

  const loginMutation = useMutation({
    mutationFn: async () => {
      const loginData: LoginRequest = {
        userName: formData.userName,
        password: formData.password,
        action: 'LOGIN',
        ...(formData.newPassword && { newPassword: formData.newPassword }),
      };
      return await apiLogin(loginData);
    },
    onSuccess: async (data) => {
      if (data.challengeName === 'NEW_PASSWORD_REQUIRED') {
        setIsNewPasswordRequired(true);
        return;
      }

      if (data.tokens) {
        localStorage.setItem('accessToken', data.tokens.accessToken);
        localStorage.setItem('refreshToken', data.tokens.refreshToken);
        localStorage.setItem('idToken', data.tokens.idToken);
        localStorage.setItem('userName', formData.userName);

        auth.setIsLoggedIn(true);
        navigate('/');
      }
    },
    onError: (error: any) => {
      if (ApiError.isApiError(error)) {
        if (error.message?.includes('NEW_PASSWORD_REQUIRED')) {
          setIsNewPasswordRequired(true);
          return;
        }

        const fieldErrors = FormErrorHandler.extractFieldErrors(error);
        
        if (Object.keys(fieldErrors).length > 0) {
          setErrors((prev) => ({ ...prev, ...fieldErrors }));
        } else {
          const errorMessage = ErrorTypeHandler.getUserFriendlyMessage(error);
          setErrors((prev) => ({
            ...prev,
            password: errorMessage,
          }));
        }
      } else {
        setErrors((prev) => ({
          ...prev,
          password: t('Login.Errors.GenericError'),
        }));
      }
    },
  });

  const forgotPasswordMutation = useMutation({
    mutationFn: async () => {
      return await forgotPassword(formData.userName);
    },
    onSuccess: () => {
      setIsForgotPassword(true);
      setSnackbar({
        open: true,
        message: t('Login.Messages.ConfirmationCodeSent'),
        severity: 'success',
      });
    },
    onError: (error: any) => {
      if (ApiError.isApiError(error)) {
        const errorMessage = ErrorTypeHandler.getUserFriendlyMessage(error);
        setErrors((prev) => ({
          ...prev,
          userName: errorMessage,
        }));
      }
    },
  });

  const confirmResetMutation = useMutation({
    mutationFn: async () => {
      return await confirmResetRequest(formData.userName, formData.confirmationCode, formData.newPassword);
    },
    onSuccess: () => {
      setSnackbar({
        open: true,
        message: t('Login.Messages.ResetSuccess'),
        severity: 'success',
      });
      setIsForgotPassword(false);
      setFormData({
        userName: '',
        password: '',
        newPassword: '',
        confirmationCode: '',
      });
    },
    onError: (error: any) => {
      if (ApiError.isApiError(error)) {
        const fieldErrors = FormErrorHandler.extractFieldErrors(error);
        
        if (Object.keys(fieldErrors).length > 0) {
          setErrors((prev) => ({ ...prev, ...fieldErrors }));
        } else {
          const errorMessage = ErrorTypeHandler.getUserFriendlyMessage(error);
          setErrors((prev) => ({
            ...prev,
            confirmationCode: errorMessage,
          }));
        }
      }
    },
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const newErrors = {
      userName: '',
      password: '',
      newPassword: '',
      confirmationCode: '',
    };

    if (!formData.userName) {
      newErrors.userName = t('Login.Errors.EmptyInput', { InputLabel: t('Login.Labels.UserName') });
    }
    if (isNewPasswordRequired || isForgotPassword) {
      if (!formData.newPassword) {
        newErrors.newPassword = t('Login.Errors.EmptyInput', { InputLabel: t('Login.Labels.NewPassword') });
      } else {
        const passwordError = validatePassword(formData.newPassword);
        if (passwordError) newErrors.newPassword = passwordError;
      }
    }
    if (isForgotPassword && !formData.confirmationCode) {
      newErrors.confirmationCode = t('Login.Errors.EmptyInput', { InputLabel: t('Login.Labels.ConfirmationCode') });
    }
    if (!isForgotPassword && !formData.password) {
      newErrors.password = t('Login.Errors.EmptyInput', { InputLabel: t('Login.Labels.Password') });
    }

    if (Object.values(newErrors).some((err) => err)) {
      setErrors(newErrors);
      return;
    }

    if (isForgotPassword) {
      confirmResetMutation.mutate();
    } else {
      loginMutation.mutate();
    }
  };

  const handleForgotPassword = () => {
    if (!formData.userName) {
      setErrors((prev) => ({
        ...prev,
        userName: t('Login.Errors.EmptyInput', { InputLabel: t('Login.Labels.UserName') }),
      }));
      return;
    }
    forgotPasswordMutation.mutate();
  };

  const inputs = isForgotPassword
    ? [
        {
          name: 'userName',
          label: t('Login.Labels.UserName'),
          type: 'text',
          value: formData.userName,
          error: errors.userName,
        },
        {
          name: 'confirmationCode',
          label: t('Login.Labels.ConfirmationCode'),
          type: 'text',
          value: formData.confirmationCode,
          error: errors.confirmationCode,
        },
        {
          name: 'newPassword',
          label: t('Login.Labels.NewPassword'),
          type: 'password',
          value: formData.newPassword,
          error: errors.newPassword,
        },
      ]
    : [
        {
          name: 'userName',
          label: t('Login.Labels.UserName'),
          type: 'text',
          value: formData.userName,
          error: errors.userName,
        },
        {
          name: 'password',
          label: t('Login.Labels.Password'),
          type: 'password',
          value: formData.password,
          error: errors.password,
        },
        ...(isNewPasswordRequired
          ? [
              {
                name: 'newPassword',
                label: t('Login.Labels.NewPassword'),
                type: 'password',
                value: formData.newPassword,
                error: errors.newPassword,
              },
            ]
          : []),
      ];

  return (
    <div className={styles.loginContainer}>
      {(loginMutation.isPending || forgotPasswordMutation.isPending || confirmResetMutation.isPending) && <Spinner />}
      <h2 style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
        {isForgotPassword
          ? t('Login.Titles.TitleForgotPassword')
          : isNewPasswordRequired
          ? t('Login.Titles.TitleNewPassword')
          : t('Login.Titles.Title')}
      </h2>
      <GenericForm
        inputs={inputs}
        onChange={handleInputChange}
        onSubmit={handleFormSubmit}
        submitLabel={(isForgotPassword || isNewPasswordRequired) ? t('General.Labels.Confirm') : t('Login.Titles.Title')}
      />
      {isForgotPassword ? (
        <button onClick={() => setIsForgotPassword(false)}>
          {t('General.Labels.Back')}
        </button>
      ) : (
        <button onClick={handleForgotPassword}>
          {t('Login.Labels.ForgotPassword')}
        </button>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default Login;

